/**
 * โมเดลจริง (.glb) ที่แปลงจากภาพ Gemini — ขั้นที่ 2 ของ GDD หัวข้อ 11
 *
 * **วางไฟล์แล้วเกมใช้เอง ไม่ต้องแก้โค้ด**: `client/src/assets/models/monsters/<id>.glb`
 * (id ตาม monsters.json) ตัวไหนยังไม่มีไฟล์ก็ใช้รูปทรงพื้นฐานเดิมต่อไป
 * รายการไฟล์มาจาก `import.meta.glob` ตอน build จึงไม่ยิงขอไฟล์ที่ไม่มี (ไม่มี 404 รก console)
 *
 * วิธีสลับ: สร้างโมเดลรูปทรงพื้นฐานตามเดิมก่อน (sync, ไม่ต้องรอ) แล้วโหลดไฟล์ทีหลัง
 * โหลดเสร็จค่อยซ่อนรูปทรงเดิมใต้ `body` แล้วเอาโมเดลไปเกาะ `body` แทน
 * ท่าเด้ง/ลอยใน `animateMonster` ขยับ `body` อยู่แล้ว จึงใช้ได้กับโมเดลใหม่ทันที
 *
 * รายการไฟล์อยู่ที่ `model-files.ts` แยกออกไป เพราะ `import.meta.glob` มีแค่ใน Vite
 * ไฟล์นี้จึง import ในเทสต์ (tsx + NullEngine) ได้
 */

import { LoadAssetContainerAsync } from "@babylonjs/core/Loading/sceneLoader";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import type { PBRMaterial } from "@babylonjs/core/Materials/PBR/pbrMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import type { AssetContainer } from "@babylonjs/core/assetContainer";
import type { Scene } from "@babylonjs/core/scene";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import type { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import type { ChibiModel, MonsterModel } from "./models.js";
import { buildRig } from "./rig.js";
import { fitTransform, type GlbTweak } from "./glb-fit.js";
import type { CharacterLook } from "@wickhaven/shared";

/** ปรับเฉพาะตัวที่หันผิดทางหรือใหญ่/เล็กไปหลังลองในเกม (ค่าเริ่มต้นคือไม่ต้องปรับ) */
const MONSTER_TWEAKS: Record<string, GlbTweak> = {
  // โมเดลจาก TRELLIS รวมก้านใบไว้ในกล่องรอบตัว ด้านที่ยาวที่สุดเลยนับจากปลายใบ ทำให้ตัวลำตัวจริงดูเล็ก
  // แต่กินพื้นที่สายตาเยอะเกินมอน Lv1 — ลดขนาดลงให้ใกล้เคียงทรงกลมเดิม (เส้นผ่าน 0.9) มากขึ้น
  slime: { scale: 0.7 },
  // สัตว์สี่ขาตัวยาว ด้านที่ยาวที่สุดของกล่องรอบตัวคือความยาวหัวถึงหาง (แนวนอน) ไม่ใช่ความสูง
  // คิดขนาดจากความยาวนั้นแล้วความสูงจริงเลยเตี้ยลงไปด้วย ดูเหมือนลูกหมาป่ามากกว่าตัวเต็มวัย
  wolf: { scale: 1.6 },
  // ปีกกางเต็มที่ทำให้ด้านที่ยาวที่สุดของกล่องรอบตัวคือช่วงปีก ไม่ใช่ความยาวตัว
  // คิดขนาดจากช่วงปีกแล้วตัวจริงเลยใหญ่เกินไปทั้งตัว (ควรเป็นมอนตัวเล็กบินปราดเปรียว)
  bat: { scale: 0.85 },
};

/**
 * โหลดครั้งเดียวต่อฉากต่อชนิด แล้วสร้างสำเนาให้แต่ละตัว (ใช้ geometry กับวัสดุร่วมกัน)
 * แคชตามฉาก เพราะวัสดุผูกกับฉากที่สร้างมัน (กับดักข้อสามใน AGENTS.md)
 */
const cache = new WeakMap<Scene, Map<string, Promise<AssetContainer | null>>>();

/**
 * ตัวอ่าน glTF (รวม PBR และส่วนขยาย Draco/Meshopt) ใหญ่ราว 170KB หลังบีบอัด
 * โหลดแยกชิ้นเฉพาะตอนมีไฟล์ให้โหลดจริง คนที่ยังไม่มีโมเดลสักไฟล์จึงไม่ต้องดาวน์โหลด
 */
let loaders: Promise<unknown> | undefined;
function ensureLoaders(): Promise<unknown> {
  return (loaders ??= Promise.all([
    import("@babylonjs/loaders/glTF/glTFFileLoader"),
    import("@babylonjs/loaders/glTF/2.0"),
  ]));
}

/** เช็คจากชื่อคลาสแทน instanceof เพื่อไม่ต้อง import PBRMaterial เข้าไฟล์หลัก */
function isPbr(m: unknown): m is PBRMaterial {
  return (m as { getClassName?: () => string } | null)?.getClassName?.() === "PBRMaterial";
}

function load(scene: Scene, id: string, url: string): Promise<AssetContainer | null> {
  let perScene = cache.get(scene);
  if (!perScene) cache.set(scene, (perScene = new Map()));
  let p = perScene.get(id);
  if (!p) {
    p = ensureLoaders()
      .then(() => LoadAssetContainerAsync(url, scene))
      .then((c) => {
        toFlatMaterials(scene, c);
        return c;
      })
      .catch((err) => {
        console.warn(`[glb] โหลด ${id} ไม่สำเร็จ ใช้รูปทรงเดิมต่อ`, err);
        return null;
      });
    perScene.set(id, p);
  }
  return p;
}

/**
 * glTF มาเป็นวัสดุ PBR ซึ่งต้องมี environment map ถึงจะสว่างถูก ฉากเราไม่มี (ใช้แสงธรรมดา)
 * ถ้าปล่อยไว้ โมเดลจะมืดทึบไม่เข้ากับของรอบข้าง จึงแปลงเป็น StandardMaterial แบบเดียวกับทั้งฉาก
 * เก็บแค่สี/ลายพื้นผิว (ภาพจาก image-to-3D มีแสงเงาอบมาในลายอยู่แล้ว) ไม่เอาความเงา
 */
function toFlatMaterials(scene: Scene, c: AssetContainer): void {
  const swap = new Map<PBRMaterial, StandardMaterial>();
  for (const m of c.materials) {
    if (!isPbr(m)) continue;
    const s = new StandardMaterial(`${m.name}-flat`, scene);
    s.diffuseTexture = m.albedoTexture;
    s.diffuseColor = m.albedoTexture ? Color3.White() : m.albedoColor.clone();
    s.emissiveTexture = m.emissiveTexture;
    s.emissiveColor = m.emissiveColor.clone();
    s.specularColor = Color3.Black();
    s.alpha = m.alpha;
    s.backFaceCulling = m.backFaceCulling;
    if (m.albedoTexture?.hasAlpha) s.useAlphaFromDiffuseTexture = true;
    swap.set(m, s);
  }
  for (const mesh of c.meshes) {
    const s = isPbr(mesh.material) ? swap.get(mesh.material) : undefined;
    if (s) mesh.material = s;
  }
  for (const [pbr, s] of swap) {
    c.materials.splice(c.materials.indexOf(pbr), 1, s);
    // ไม่เคยถูกวาด จึงทิ้งได้ แต่ห้ามทิ้งลายพื้นผิว เพราะวัสดุใหม่ใช้ลายเดียวกันต่อ
    pbr.dispose(false, false);
  }
}

/**
 * สลับมอนตัวนี้เป็นโมเดลจริงจากไฟล์ `url` (หาจาก `monsterModelUrl` ใน model-files.ts)
 * `size` คือขนาดที่เกมตั้งไว้ (ขนาดตัวรับคลิก) ไม่ต้อง await ก็ได้ ระหว่างโหลดยังเห็นรูปทรงเดิม
 */
export async function upgradeMonster(
  scene: Scene,
  model: MonsterModel,
  type: string,
  size: number,
  url: string,
): Promise<void> {
  const container = await load(scene, type, url);
  // มอนตายหรือออกจากฉากระหว่างโหลด
  if (!container || model.root.isDisposed()) return;

  const inst = container.instantiateModelsToScene((n) => `${type}:${n}`, false, { doNotInstantiate: true });
  const top = inst.rootNodes[0];
  if (!top) return;

  for (const m of top.getChildMeshes(false)) m.computeWorldMatrix(true);
  const { min, max } = top.getHierarchyBoundingVectors(true);
  const fit = fitTransform(
    { x: min.x, y: min.y, z: min.z },
    { x: max.x, y: max.y, z: max.z },
    size,
    MONSTER_TWEAKS[type],
  );

  // ห่อด้วย node ของเราเอง ไม่ไปทับ rotation/scaling ที่ตัวโหลดใส่ไว้ที่ __root__ เพื่อแปลงแกน glTF
  const wrap = new TransformNode(`${type}-glb`, scene);
  top.parent = wrap;
  wrap.scaling.setAll(fit.scale);
  wrap.position = new Vector3(fit.x, fit.y, fit.z);
  wrap.rotation.y = fit.yaw;

  for (const m of model.body.getChildMeshes(false)) m.setEnabled(false);
  for (const m of top.getChildMeshes(false)) m.isPickable = false;
  wrap.parent = model.body;
  // มอนสองขาที่ใส่กระดูกด้วย tools/autorig.py ขยับแขนขาได้ ตัวอื่นยังเด้งทั้งตัวเหมือนเดิม
  model.rig = buildRig(top);
}

/** ปรับแต่งเฉพาะตัวละคร key = `<อาชีพ>_<เพศ>` (ค่าเริ่มต้นคือไม่ต้องปรับ) */
const CHARACTER_TWEAKS: Record<string, GlbTweak> = {};

/**
 * ขนาดตัวละคร .glb (ด้านยาวสุด = ค่านี้ × SIZE_FILL) เดิม 1.85 สูงกว่าตัวชิบิและ NPC ชัดเจน (ผู้ใช้ทดสอบ 8 ต.ค. 2026)
 * ตัว .glb ขายาวกว่าชิบิ ความสูงเท่ากันจึงดูใหญ่กว่า ต้องเตี้ยกว่าตัวชิบิรูปทรงพื้นฐาน
 */
const CHARACTER_SIZE = 1.4;

/**
 * ลายพื้นผิวจาก image-to-3D อบแสงเงามาแล้ว พอฉากคูณแสงซ้ำ (hemi 0.8 + sun 0.5) ด้านข้าง/หลังจะมืดคล้ำ
 * ให้ลายพื้นผิวเรืองเองส่วนหนึ่ง สีจึงใกล้ภาพต้นฉบับ ใช้กับตัวละครเท่านั้น (มอนยังไม่มีคนบ่น)
 */
const CHARACTER_SELF_LIGHT = 0.55;
const brightened = new WeakSet<AssetContainer>();
function brighten(c: AssetContainer): void {
  if (brightened.has(c)) return;
  brightened.add(c);
  for (const m of c.materials) {
    const s = m as StandardMaterial;
    if (s.getClassName?.() !== "StandardMaterial" || !s.diffuseTexture) continue;
    s.emissiveTexture = s.diffuseTexture;
    s.emissiveColor = new Color3(CHARACTER_SELF_LIGHT, CHARACTER_SELF_LIGHT, CHARACTER_SELF_LIGHT);
  }
}

/**
 * ย้อมสีตามชื่อ material (ไม่สนตัวพิมพ์): Skin = สีผิว · Hair = สีผม (คูณกับ texture เทาอ่อน) · Eyes = สีตา
 * วัสดุถูกแชร์ระหว่างตัวละครที่ใช้ไฟล์เดียวกัน (doNotInstantiate) → **โคลนต่อตัว** แล้วทิ้งตอนโมเดลถูกลบ
 * ห้ามแก้วัสดุที่แชร์ตรง ๆ ไม่งั้นทุกคนสีเดียวกันหมด (กับดักเดียวกับ "ห้ามลบวัสดุร่วม" ใน AGENTS)
 */
export function tintByMaterialName(meshes: AbstractMesh[], custom: CharacterLook, owner: TransformNode): void {
  const made: StandardMaterial[] = [];
  const cloned = new Map<unknown, StandardMaterial>();
  for (const m of meshes) {
    const src = m.material as StandardMaterial | null;
    if (!src || typeof (src as { diffuseColor?: unknown }).diffuseColor === "undefined") continue;
    const n = src.name.toLowerCase();
    const color = n.includes("skin") ? custom.skin : n.includes("hair") ? custom.hair : n.includes("eye") ? custom.eyes : undefined;
    if (!color) continue;
    let mat = cloned.get(src);
    if (!mat) {
      mat = src.clone(`${src.name}#${owner.uniqueId}`) as StandardMaterial;
      mat.diffuseColor = Color3.FromHexString(color);
      // ผิว/ตา: สีเรียบตามที่เลือก · ผม: คงลาย texture เทาไว้ให้มีมิติ (คูณสี)
      if (!n.includes("hair")) mat.diffuseTexture = null;
      cloned.set(src, mat);
      made.push(mat);
    }
    m.material = mat;
  }
  if (made.length) owner.onDisposeObservable.addOnce(() => { for (const x of made) x.dispose(false, false); });
}

/**
 * สลับโมเดลตัวละครเป็นโมเดลจริงจากไฟล์ .glb
 */
export async function upgradeCharacter(
  scene: Scene,
  model: ChibiModel,
  classId: string,
  gender: string,
  url: string,
  /** หน้าตาที่เลือกเอง: ย้อม material ชื่อ Skin / Hair / Eyes (สเปกใน docs/GEMINI-PROMPTS.md) */
  custom?: CharacterLook,
): Promise<void> {
  const key = `${classId}_${gender}`;
  const container = await load(scene, key, url);
  if (!container || model.root.isDisposed()) return;
  brighten(container);

  const inst = container.instantiateModelsToScene((n) => `${key}:${n}`, false, { doNotInstantiate: true });
  const top = inst.rootNodes[0];
  if (!top) return;

  for (const m of top.getChildMeshes(false)) m.computeWorldMatrix(true);
  const { min, max } = top.getHierarchyBoundingVectors(true);
  const fit = fitTransform(
    { x: min.x, y: min.y, z: min.z },
    { x: max.x, y: max.y, z: max.z },
    CHARACTER_SIZE,
    CHARACTER_TWEAKS[key],
  );

  const wrap = new TransformNode(`${key}-glb`, scene);
  top.parent = wrap;
  wrap.scaling.setAll(fit.scale);
  wrap.position = new Vector3(fit.x, fit.y, fit.z);
  wrap.rotation.y = fit.yaw;

  // ซ่อนชิ้นตัวชิบิเดิม (หัว ลำตัว แขน ขา) แต่ไม่แตะของสวมใส่ใต้จุดแขวน
  // โมเดลที่มีกระดูกยังใช้ระบบอาวุธ/ของสวมใส่เดิม จุดแขวนในมือย้ายตามกระดูกมือ (animateChibi)
  const rig = buildRig(top);
  if (rig) {
    // อาวุธประจำอาชีพเกาะแขนชิบิตรง ๆ ย้ายไปเกาะจุดแขวนมือ (จุดแขวนอยู่ตำแหน่งเดียวกับแขนพอดี
    // การย้ายจึงไม่เปลี่ยนท่าถือ) จุดแขวนถูกยกไปตามกระดูกมือจริงทุกเฟรมใน animateChibi
    for (const w of model.classWeapon) {
      const left = w.parent === model.arms[0];
      w.parent = left ? model.anchors.handL : model.anchors.handR;
    }
  }
  const anchorNodes = Object.values(model.anchors);
  const isGear = (m: AbstractMesh) => anchorNodes.some((a) => m.isDescendantOf(a));
  for (const m of model.body.getChildMeshes(false)) {
    if (!isGear(m)) m.isVisible = false;
  }
  for (const leg of model.legs) {
    leg.isVisible = false;
    for (const m of leg.getChildMeshes(false)) m.isVisible = false;
  }
  if (!rig) {
    // ไฟล์ยังไม่มีกระดูก: ตัวแข็งทื่อขยับไม่ได้ ซ่อนของในมือไปด้วยดีกว่าลอยกลางอากาศ
    for (const m of model.body.getChildMeshes(false)) m.setEnabled(false);
  }
  for (const m of top.getChildMeshes(false)) m.isPickable = false;
  if (custom) tintByMaterialName(top.getChildMeshes(false), custom, model.root);

  wrap.parent = model.body;
  model.rig = rig;
}

